import React from 'react';
import {createGlobalStyle} from 'styled-components';
import {Transition, TransitionProps} from '../Transition';
import {getTimeouts} from '../utils';

export type GrowProps = Pick<
  TransitionProps,
  'mounted' | 'duration' | 'delay'
> & {
  direction?: 'up' | 'down' | 'left' | 'right';
};

export const Grow: React.FC<React.PropsWithChildren<GrowProps>> = ({
  children,
  mounted,
  duration,
  delay,
  direction = 'left',
}) => {
  const nodeRef = React.useRef(null);

  const timeouts = getTimeouts({duration, delay});
  const animationDuration = mounted
    ? timeouts.mountDuration
    : timeouts.unmountDuration;
  const animationDelay = mounted ? timeouts.mountDelay : timeouts.unmountDelay;

  const styles = {
    transformOrigin: `center ${direction}`,
    animation: `${
      mounted ? 'enterGrow' : 'exitShrink'
    } ${animationDuration}ms ease-in-out ${animationDelay}ms forwards`,
  };

  return (
    <Transition
      nodeRef={nodeRef}
      mounted={mounted}
      duration={duration}
      delay={delay}
    >
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement, {
            style: {
              ...(child.props?.style ? child.props.style : {}),
              ...styles,
            },
            ref: nodeRef,
          });
        }
        return null;
      })}
      <Animations />
    </Transition>
  );
};

const Animations = createGlobalStyle`
  @keyframes enterGrow {
    from { opacity: 0; transform: scale(0); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes exitShrink {
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0; transform: scale(0); }
  }
`;
